import os from 'node:os';
import { getLanguageServerVersion } from '@gitlab-org/core';
import {
  type EnvInfo,
  type DuoCliDistribution,
  DUO_CLI_DISTRIBUTIONS,
  detectTerminal,
  detectShell,
  isKittyProtocolSupported,
  detectTerminalTheme,
  isGitlabCi,
} from '@gitlab-org/tui';

const getEnvironment = (): EnvironmentLabel => {
  // @ts-ignore
  return BUNDLER_INJECTED_ENVIRONMENT;
};

export const getDistribution = (): DuoCliDistribution => {
  // GITLAB_DUO_DISTRIBUTION env var takes precedence (set by glab when invoking duo-cli)
  const envValue = process.env.GITLAB_DUO_DISTRIBUTION;
  if (envValue) {
    if ((DUO_CLI_DISTRIBUTIONS as readonly string[]).includes(envValue)) {
      return envValue as DuoCliDistribution;
    }
    // eslint-disable-next-line no-console
    console.warn(`Unknown GITLAB_DUO_DISTRIBUTION value "${envValue}", falling back to "unknown"`);
    return 'unknown';
  }

  // @ts-ignore
  return BUNDLER_INJECTED_DISTRIBUTION;
};

export type EnvironmentLabel = 'development' | 'production';

/**
 * Environment context sent to server-side telemetry as a JSON string via the
 * `x-gitlab-tracking-context` header. Extend this object to forward additional
 * context without touching backend layers (see work item #2655).
 */
export interface DuoCliContext {
  distribution: DuoCliDistribution;
  /**
   * Where the CLI ran. `gitlab_ci` is detected via `GITLAB_CI` only (see
   * {@link isGitlabCi}); every other environment (local shells, and CI systems
   * such as GitHub Actions or Jenkins) reports `other`. The vocabulary is
   * deliberately narrow so dashboards don't read `other` as "not any CI".
   */
  execution_environment: 'gitlab_ci' | 'other';
}

export const getDuoCliContext = (): DuoCliContext => ({
  distribution: getDistribution(),
  execution_environment: isGitlabCi() ? 'gitlab_ci' : 'other',
});

/**
 * Maximum byte size of the serialized context. HTTP header blocks are commonly
 * capped around 8 KB by servers/proxies (e.g. nginx `large_client_header_buffers`);
 * this leaves ample headroom for the rest of the request headers.
 */
export const MAX_DUO_CLI_CONTEXT_BYTES = 2048;

/** HTTP header values must be visible ASCII plus space (RFC 7230). */
const HEADER_SAFE_ASCII = /^[\x20-\x7e]*$/;

/**
 * Result of building the `x-gitlab-tracking-context` header. A discriminated
 * union so the invariant is enforced at the type level: on success only
 * `value` is present, and on rejection only `reason` is present.
 */
export type DuoCliContextHeaderResult =
  | { value: string; reason?: undefined }
  | { value?: undefined; reason: string };

/**
 * Serialize a {@link DuoCliContext} to a plain-JSON `x-gitlab-tracking-context`
 * header value, validating that it is safe to send as an HTTP header. Plain
 * JSON (rather than Base64) keeps the payload transparent for
 * tracking-transparency reasons (see work item #2655).
 *
 * Returns `{ reason }` (and no `value`, so the header is omitted) when the
 * serialized payload contains non-ASCII/control bytes or exceeds
 * {@link MAX_DUO_CLI_CONTEXT_BYTES}. The header is best-effort telemetry and
 * must never break the underlying request; the validation guards future,
 * free-form context fields.
 */
export const serializeDuoCliContextHeader = (context: DuoCliContext): DuoCliContextHeaderResult => {
  const value = JSON.stringify(context);

  if (!HEADER_SAFE_ASCII.test(value)) {
    return { reason: 'contains non-ASCII or control characters' };
  }

  const byteLength = Buffer.byteLength(value, 'utf8');
  if (byteLength > MAX_DUO_CLI_CONTEXT_BYTES) {
    return { reason: `exceeds ${MAX_DUO_CLI_CONTEXT_BYTES} bytes (was ${byteLength})` };
  }

  return { value };
};

/** Build the validated `x-gitlab-tracking-context` header from the current environment. */
export const buildDuoCliContextHeader = (): DuoCliContextHeaderResult =>
  serializeDuoCliContextHeader(getDuoCliContext());

let envInfo: EnvInfo | undefined;

/** Static environment facts that never depend on probing the terminal. */
type TerminalCapabilities = Pick<EnvInfo, 'isKittyProtocolSupported' | 'theme'>;

const buildEnvInfo = (capabilities: TerminalCapabilities): EnvInfo => ({
  terminalName: detectTerminal(),
  isKittyProtocolSupported: capabilities.isKittyProtocolSupported,
  theme: capabilities.theme,
  duoCliVersion: getLanguageServerVersion(),
  environment: getEnvironment(),
  distribution: getDistribution(),
  osPlatform: os.type(),
  osVersion: os.release(),
  shell: detectShell(),
});

/**
 * Environment info for interactive commands (TUI, config editor) that render a
 * live terminal UI. Probes the terminal for kitty-protocol support and theme.
 * These probes WRITE query escape sequences to the terminal, which is safe for
 * an interactive surface but would corrupt machine-readable output (see
 * {@link getHeadlessEnvInfo}).
 */
export const getInteractiveEnvInfo = async (): Promise<EnvInfo> => {
  if (!envInfo) {
    envInfo = buildEnvInfo({
      isKittyProtocolSupported: await isKittyProtocolSupported(),
      theme: await detectTerminalTheme(),
    });
  }

  return envInfo;
};

/**
 * Environment info for headless commands (e.g. `run`) that never render a live
 * terminal UI. Skips the terminal-capability probes and uses safe defaults:
 * the probes emit query escape sequences when stdin/stdout is a TTY (e.g. under
 * a PTY allocated by `glab duo cli`), which would corrupt the command's output
 * stream.
 */
export const getHeadlessEnvInfo = async (): Promise<EnvInfo> => {
  if (!envInfo) {
    envInfo = buildEnvInfo({ isKittyProtocolSupported: false, theme: 'dark' });
  }

  return envInfo;
};
